import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, MessageSquareIcon } from '@utils/icons';
import { Avatar } from '@components/ui/Avatar/Avatar';
import { Modal } from '@components/ui/Modal/Modal';
import type { ChannelSummary, CommsMember } from '@interfaces/ICommsApi';
import { memberName } from '../../utils/memberName';
import info from '../infoDialog.module.css';
import styles from './ConversationInfoDialog.module.css';

type Channel = Extract<ChannelSummary, { kind: 'channel' }>;

export interface IConversationInfoDialogProps {
  open: boolean;
  channel: ChannelSummary;
  label: string;
  currentUserId: string;
  members: CommsMember[];
  channels: ChannelSummary[];
  onClose: () => void;
  onMessageMember: (userId: string) => void;
  onOpenChannel: (channelId: string) => void;
}

/**
 * What the conversation is, over the conversation itself — the web's
 * counterpart of mobile's `ConversationInfoSheet`: a channel's creator and
 * members, each a step away from a direct message; a direct message's person
 * and the channels you share.
 */
export function ConversationInfoDialog(props: IConversationInfoDialogProps) {
  const { open, channel, label, onClose } = props;
  return (
    <Modal open={open} onClose={onClose} title={label} size="sm">
      {channel.kind === 'channel' ? <ChannelInfo {...props} channel={channel} /> : <DirectMessageInfo {...props} />}
    </Modal>
  );
}

function ChannelInfo({ channel, currentUserId, members, onClose, onMessageMember }: IConversationInfoDialogProps & { channel: Channel }) {
  const { t, i18n } = useTranslation();
  const creator = members.find((m) => m.id === channel.creatorId);
  // The viewer's zone, like every time in comms.
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(channel.createdAt));

  // The creator first, then everyone by name, you last.
  const roster = useMemo(() => {
    const rank = (m: CommsMember) => (m.id === channel.creatorId ? 0 : m.id === currentUserId ? 2 : 1);
    return members
      .filter((m) => channel.memberIds.includes(m.id))
      .sort((a, b) => rank(a) - rank(b) || memberName(a).localeCompare(memberName(b)));
  }, [members, channel.memberIds, channel.creatorId, currentUserId]);

  return (
    <>
      {date && (
        <p className={styles.meta}>
          {creator ? t('comms.info.createdBy', { name: memberName(creator), date }) : t('comms.info.createdOn', { date })}
        </p>
      )}
      <h3 className={info.sectionTitle}>{t('comms.header.members', { count: roster.length })}</h3>
      <ul className={info.list}>
        {roster.map((m) => {
          const you = m.id === currentUserId;
          return (
            <li key={m.id}>
              <button
                type="button"
                className={styles.row}
                disabled={you}
                onClick={() => { onMessageMember(m.id); onClose(); }}
                data-testid={`info-member-${m.id}`}
              >
                <Avatar name={memberName(m)} size={28} tinted />
                <span className={info.name}>{memberName(m)}</span>
                {m.id === channel.creatorId && <span className={styles.badge}>{t('comms.info.creator')}</span>}
                {you
                  ? <span className={styles.you}>{t('comms.messages.youTag')}</span>
                  : <span className={styles.rowIcon} aria-hidden="true"><MessageSquareIcon size={14} /></span>}
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function DirectMessageInfo({ channel, currentUserId, channels, onClose, onOpenChannel }: IConversationInfoDialogProps) {
  const { t } = useTranslation();
  const otherId = channel.memberIds.find((id) => id !== currentUserId);
  const shared = channels.filter(
    (c): c is Channel => c.kind === 'channel' && c.memberIds.includes(currentUserId) && otherId !== undefined && c.memberIds.includes(otherId),
  );

  return (
    <>
      <h3 className={info.sectionTitle}>{t('comms.info.sharedChannels')}</h3>
      {shared.length === 0 ? (
        <p className={info.muted}>{t('comms.info.noSharedChannels')}</p>
      ) : (
        <ul className={info.list}>
          {shared.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className={styles.row}
                onClick={() => { onOpenChannel(c.id); onClose(); }}
                data-testid={`info-channel-${c.id}`}
              >
                <span className={styles.tile} aria-hidden="true">#</span>
                <span className={info.name}>{c.name}</span>
                <span className={styles.you}>{t('comms.header.members', { count: c.memberCount })}</span>
                <span className={styles.rowIcon} aria-hidden="true"><ChevronRightIcon size={14} /></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
